import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { routes } from '@const/routes';
import { ENewNotificationType } from '@enum';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { apiOrderService, apiTransactionsService } from '@api';
import {
    TTransaction,
    ETransactionAction,
    ETransactionStatus,
    ETransactionType
} from '@xeppt/xeppt-sdk/types';
import { useDebounceValue } from 'usehooks-ts';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import Status from '@components/common/status';
import Skeleton from '@components/common/skeleton';
import Button from '@components/common/button';
import { prettifyAmount, formatDate } from '@utils/index';
import DevPageHeader from '../_shared/PageHeader';
import FilterDrawer, { type MoneyMovementValue, type SortDir } from '../_shared/FilterDrawer';
import PurchaseDetailDrawer, { type PurchaseDetailOrderInfo } from '../_shared/PurchaseDetailDrawer';
import styles from './styles.module.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

// order_id/description/points are flattened backend fields the SDK's
// TTransaction type doesn't declare yet — same "backend field, SDK type
// lags" pattern as sales/index.tsx's TSaleTransaction. Both consume the
// exact same findBusinessSales endpoint (Payments and Sales show the same
// underlying transaction data; only the page chrome/filters differ).
type TPaymentTxn = TTransaction & {
    order_id?: string;
    description?: string;
    points?: number;
};

type FetchParams = {
    isNew?: boolean;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    movement?: MoneyMovementValue;
};

type PaymentsResponse = {
    data: TPaymentTxn[];
    page: number;
    page_size: number;
    pages_total: number;
    total?: number;
    total_received: number;
    total_approved: number;
    total_failed: number;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

const PAYMENTS_STATUS_CHIPS: { label: string; value: ETransactionStatus }[] = [
    { label: 'Approved', value: ETransactionStatus.APPROVED },
    { label: 'Pending', value: ETransactionStatus.PENDING },
    { label: 'Canceled', value: ETransactionStatus.CANCELED },
    { label: 'Declined', value: ETransactionStatus.DECLINED }
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function addDatetime(date: string, time: string): string {
    return `${date}T${time}.000Z`;
}

// The backend's `page` response field is a pure echo of the request — never
// server-incremented — so trusting it (via a `data.page`-derived
// `currentPage` state) made infinite scroll stick on page 1 forever: every
// "load more" re-requested the exact page it had just received. The next
// page must instead be derived from how many rows are already loaded. A
// missing request (the initial mount fetch, and the notification-triggered
// refetch below) or an explicit `isNew` (filter apply/retry/search) both
// mean "start over from page 1"; anything else is a genuine load-more
// continuation from the scroll observer. Exported so this fix is
// independently regression-tested.
export function isFreshPageRequest(req?: FetchParams): boolean {
    return !req || !!req.isNew;
}

// See isFreshPageRequest above — never derive the next page from the
// backend's echoed `page` field.
export function computeNextPage(req: FetchParams | undefined, loadedCount: number): number {
    return isFreshPageRequest(req) ? 1 : Math.floor(loadedCount / PAGE_SIZE) + 1;
}

// 'Money in' (received by the merchant) is a DEBIT leg; 'Money out' (a
// refund leaving the merchant) is a CREDIT leg — the same DEBIT=merchant-leg
// convention used across the dev portal (see PurchaseDetailDrawer's
// isReceived / orders/index.tsx's buildMergedTxnRows). Exported so the
// mapping direction is independently regression-tested rather than only
// implied by inline logic in the fetch method below.
export function movementToTransactionType(
    movement: MoneyMovementValue
): ETransactionType | undefined {
    if (movement === 'in') return ETransactionType.DEBIT;
    if (movement === 'out') return ETransactionType.CREDIT;
    return undefined;
}

function parseSource(source: string): { displayName: string; handle: string; initials: string } {
    const match = /^(.*?)\s*\((@[^)]+)\)$/.exec(source);
    if (!match) {
        return { displayName: source, handle: '', initials: source.slice(0, 2).toUpperCase() };
    }
    const name = match[1].trim();
    const parts = name.split(/\s+/);
    const initials =
        parts.length >= 2
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
            : name.slice(0, 2).toUpperCase();
    return { displayName: name, handle: match[2], initials };
}

// Mirrors sales/index.tsx's txnSecondaryLabel exactly — same data source
// (findBusinessSales), same action-first discriminator (a refund leg is
// Action=REFUND/Type=CREDIT; a sale leg is Action=PAYMENT/Type=DEBIT).
export function txnSecondaryLabel(tx: TTransaction): string {
    if (tx.action === ETransactionAction.REFUND) return 'Refund';
    if (tx.status === ETransactionStatus.RETURNED) return 'Purchase. Refunded';
    return 'Purchase';
}

export function isRefundTxn(tx: TTransaction): boolean {
    return tx.action === ETransactionAction.REFUND;
}

function txnStatusLabel(status: ETransactionStatus): string {
    switch (status) {
        case ETransactionStatus.PENDING: return 'Pending';
        case ETransactionStatus.APPROVED: return 'Approved';
        case ETransactionStatus.DECLINED: return 'Declined';
        case ETransactionStatus.CANCELED: return 'Canceled';
        case ETransactionStatus.REFUND: return 'Refund';
        case ETransactionStatus.ERROR: return 'Error';
        case ETransactionStatus.RETURNED: return 'Returned';
        case ETransactionStatus.EXPIRED: return 'Expired';
        default: return 'Unknown';
    }
}

function txnStatusVariant(status: ETransactionStatus): 'success' | 'warning' | 'error' | 'gray' {
    switch (status) {
        case ETransactionStatus.APPROVED: return 'success';
        case ETransactionStatus.PENDING: return 'warning';
        case ETransactionStatus.DECLINED:
        case ETransactionStatus.ERROR:
        case ETransactionStatus.CANCELED: return 'error';
        default: return 'gray';
    }
}

function shouldShowStatusBadge(status: ETransactionStatus): boolean {
    return (
        status === ETransactionStatus.PENDING ||
        status === ETransactionStatus.DECLINED ||
        status === ETransactionStatus.CANCELED
    );
}

// Builds the lightweight order-info bag PurchaseDetailDrawer needs, straight
// from the already-fetched list row — no second network request.
//
// Dual-reads order_id/description: flattened top-level fields first (the
// shape sales/index.tsx's TSaleTransaction assumes), falling back to the
// details.order_id/order_description nesting the SDK's TTransactionDetails
// type still declares — same flat-then-details fallback convention used
// elsewhere for this same SDK-lag pattern (e.g. the customer-facing txn
// row's counterparty_avatar).
//
// Counterparty always resolves from `.source`, regardless of direction —
// per transaction_item.tsx's canonical convention: a refund leg's `source`
// is still the refunded CUSTOMER (its `destination` is the merchant's own
// wallet). Branching to `.destination` for CREDIT/refund rows would show
// the merchant's own wallet as the counterparty instead of the customer.
export function paymentsTxnOrderInfo(txn: TPaymentTxn): PurchaseDetailOrderInfo {
    const { displayName, handle } = parseSource(txn.source ?? '');
    return {
        customer_display_name: displayName || undefined,
        customer_id: handle.replace(/^@/, '') || undefined,
        currency: txn.currency,
        order_id: txn.order_id ?? txn.details?.order_id ?? undefined,
        description: txn.description ?? txn.details?.order_description ?? undefined
    };
}

// ─── Layout ──────────────────────────────────────────────────────────────────

const DevelopersPaymentsLayout = () => {
    const navigate = useNavigate();
    const { user } = useUserContext();
    const { env } = useEnvironmentContext();
    // ?id= deep-link support (e.g. a notification linking straight to a
    // transaction) — unique to Payments among the dev-portal list pages,
    // preserved from the previous implementation. `intent` is accepted but
    // intentionally unused, matching the prior behavior.
    const [id] = useQueryParams(['id', 'intent']);

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch] = useDebounceValue(searchInput, 300);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [movement, setMovement] = useState<MoneyMovementValue>('all');
    // Status chips and sort direction are tracked locally so FilterDrawer's
    // controlled UI works, but — matching this page's existing behavior —
    // are not forwarded to the query below (see the method callback's
    // comment). Left as a follow-up: wiring them in is a small, safe change
    // once product confirms it's wanted.
    const [statusChips, setStatusChips] = useState<ETransactionStatus[]>([]);
    const [sortDir, setSortDir] = useState<SortDir>('newest');

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedTxnEntry, setSelectedTxnEntry] = useState<TPaymentTxn | null>(null);

    const [transactions, setTransactions] = useState<TPaymentTxn[]>([]);
    const [trigger, setTrigger] = useState(0);
    const loader = useRef<HTMLDivElement>(null);

    const {
        data: paymentsResponse,
        isLoading,
        error,
        handleRequest: fetchPayments
    } = useApiQuery<FetchParams, PaymentsResponse>({
        method: (req?: FetchParams) => {
            const page = computeNextPage(req, transactions.length);
            const typeVal = movementToTransactionType(req?.movement ?? 'all');
            return apiOrderService.findBusinessSales({
                page,
                page_size: PAGE_SIZE,
                // Sort direction isn't wired to the query (see statusChips
                // comment above) — always newest-first, matching this
                // page's existing behavior.
                order: 'DESC',
                search: req?.search || undefined,
                date_from: req?.dateFrom ? addDatetime(req.dateFrom, '00:00:00') : undefined,
                date_to: req?.dateTo ? addDatetime(req.dateTo, '23:59:59') : undefined,
                type: typeVal !== undefined ? [typeVal] : undefined,
                environment: env
            }) as unknown as Promise<PaymentsResponse>;
        },
        onSuccess: (data, req) => {
            const incoming = (data.data ?? []) as TPaymentTxn[];
            if (isFreshPageRequest(req)) {
                setTransactions(incoming);
            } else {
                setTransactions((prev) => [...prev, ...incoming]);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION,
        condition: !!user,
        deps: [user, env]
    });

    const applyFetch = useCallback(
        (overrides?: Partial<FetchParams>) => {
            fetchPayments({
                isNew: true,
                search: debouncedSearch,
                dateFrom,
                dateTo,
                movement,
                ...overrides
            });
        },
        [fetchPayments, debouncedSearch, dateFrom, dateTo, movement]
    );

    // Opens the shared merchant transaction drawer — used both for row
    // clicks and the ?id= deep-link fetch further down.
    const handleViewTransaction = (transaction: TPaymentTxn) => {
        setIsFilterOpen(false);
        setSelectedTxnEntry(transaction);
    };

    const handleCloseTransactionDrawer = () => {
        setSelectedTxnEntry(null);
        if (id) {
            navigate(routes.developers_payments);
        }
    };

    useApiQuery({
        method: () => apiTransactionsService.getTransactionById(id || ''),
        condition: !!id,
        deps: [id],
        onSuccess: (data) => {
            handleViewTransaction(data);
        }
    });

    useEffect(() => {
        if (debouncedSearch.length === 0 || debouncedSearch.length >= 3) {
            applyFetch({ search: debouncedSearch });
        }
    }, [debouncedSearch]);

    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) setTrigger((prev) => prev + 1);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '20px',
            threshold: 0
        });
        if (loader.current) observer.observe(loader.current);
    }, [handleObserver]);

    useEffect(() => {
        const total = paymentsResponse?.total ?? 0;
        if (transactions.length < total && !isLoading) {
            fetchPayments({ search: debouncedSearch, dateFrom, dateTo, movement });
        }
    }, [trigger]);

    const handleFilterClose = () => {
        setIsFilterOpen(false);
        applyFetch();
    };

    const handleFilterReset = () => {
        setDateFrom('');
        setDateTo('');
        setMovement('all');
        setStatusChips([]);
        setSortDir('newest');
    };

    const handleToggleStatus = (s: ETransactionStatus) => {
        setStatusChips((prev) =>
            prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
        );
    };

    // Only counts filters that actually affect the query (date range, money
    // movement) — status/sort are tracked for the UI but not yet wired in,
    // see the comment above the statusChips declaration.
    const activeFilterCount =
        (dateFrom || dateTo ? 1 : 0) +
        (movement !== 'all' ? 1 : 0);

    const stats = [
        {
            title: 'Total received',
            helper: 'The total amount of successful customer purchases in the selected period',
            value: `$${prettifyAmount((paymentsResponse?.total_received ?? 0).toFixed(2))}`,
            icon: 'money' as const,
            mod: styles.stat_received
        },
        {
            title: 'Successful purchases',
            helper: 'The number of successful customer purchases in the selected period',
            value: String(paymentsResponse?.total_approved ?? 0),
            icon: 'check_outlined' as const,
            mod: styles.stat_success
        },
        {
            title: 'Failed purchases',
            helper: 'The number of failed customer purchases in the selected period',
            value: String(paymentsResponse?.total_failed ?? 0),
            icon: 'error_outlined' as const,
            mod: styles.stat_error
        }
    ];

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <DevPageHeader
                title="Payments"
                subtitle="View and reconcile payments processed through your XEPPT integration."
            />
            <div className={styles.stats}>
                {isLoading && !transactions.length ? (
                    <>
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                    </>
                ) : (
                    stats.map((item) => (
                        <div key={item.title} className={[styles.stat_card, item.mod].join(' ')}>
                            <div className={styles.stat_icon}>
                                <Icon name={item.icon} width={22} height={22} />
                            </div>
                            <div className={styles.stat_body}>
                                <div className={styles.stat_label_row}>
                                    <p className={styles.stat_label}>{item.title}</p>
                                    <Info variant="left" content={<>{item.helper}</>} />
                                </div>
                                <Typography variant="h5" weight="semibold" className={styles.stat_value}>
                                    {item.value}
                                </Typography>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div className={styles.list_card}>
                <div className={styles.toolbar}>
                    <div className={styles.search_wrap}>
                        <Icon name="search" width={16} height={16} className={styles.search_icon} />
                        <input
                            type="text"
                            placeholder="Search by customer, order ID, or reference code..."
                            value={searchInput}
                            className={styles.search_input}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                    <div className={styles.toolbar_actions}>
                        <button type="button" className={styles.download_btn}>
                            <Icon name="download" width={16} height={16} />
                            Download
                        </button>
                        <button
                            type="button"
                            className={styles.filters_btn}
                            onClick={() => setIsFilterOpen(true)}>
                            <Icon name="filter" width={16} height={16} />
                            {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
                        </button>
                    </div>
                </div>
                <div className={styles.list}>
                    {isLoading && !transactions.length &&
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={`sk-${i}`} className={styles.row_skeleton}>
                                <Skeleton className={styles.skeleton_avatar} />
                                <div className={styles.skeleton_text_block}>
                                    <Skeleton className={styles.skeleton_line_primary} />
                                    <Skeleton className={styles.skeleton_line_secondary} />
                                </div>
                                <Skeleton className={styles.skeleton_amount} />
                            </div>
                        ))}
                    {!isLoading && !!error && transactions.length === 0 && (
                        <div className={styles.empty_state}>
                            <div className={styles.empty_icon}>
                                <Icon name="error_outlined" width={26} height={26} />
                            </div>
                            <Typography variant="body3" weight="semibold">
                                Failed to load payments
                            </Typography>
                            <Typography variant="body4" className={styles.empty_desc}>
                                There was a problem fetching your transactions.
                            </Typography>
                            <Button
                                variant="outlined-primary"
                                size="small"
                                onClick={() => fetchPayments({ isNew: true })}>
                                Retry
                            </Button>
                        </div>
                    )}
                    {!isLoading && !error && transactions.length === 0 && (
                        <div className={styles.empty_state}>
                            <div className={styles.empty_icon}>
                                <Icon name="money" width={26} height={26} />
                            </div>
                            <Typography variant="body3" weight="semibold">
                                No payments yet
                            </Typography>
                            <Typography variant="body4" className={styles.empty_desc}>
                                Payments processed through your integration will appear here.
                            </Typography>
                        </div>
                    )}
                    {transactions.map((txn) => {
                        const { displayName, handle, initials } = parseSource(txn.source ?? '');
                        const refund = isRefundTxn(txn);
                        const secondaryLabel = txnSecondaryLabel(txn);
                        const showBadge = shouldShowStatusBadge(txn.status);
                        return (
                            <div
                                key={txn.id}
                                className={styles.list_row}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleViewTransaction(txn)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') handleViewTransaction(txn);
                                }}>
                                <div
                                    className={[
                                        styles.row_avatar,
                                        refund ? styles.row_avatar_out : styles.row_avatar_in
                                    ].join(' ')}>
                                    <Icon
                                        name={refund ? 'transaction_arrow_up' : 'transaction_arrow_down'}
                                        width={20}
                                        height={20}
                                    />
                                    {!displayName && (
                                        <span className={styles.row_initials}>{initials}</span>
                                    )}
                                </div>
                                <div className={styles.row_info}>
                                    <p className={styles.row_name}>
                                        {displayName || txn.source}
                                        {handle && (
                                            <span className={styles.row_handle}> {handle}</span>
                                        )}
                                    </p>
                                    <p className={styles.row_secondary}>
                                        {secondaryLabel}
                                        <span className={styles.row_dot}> · </span>
                                        {formatDate(txn.created_at, 'DD MMM YYYY')}
                                    </p>
                                </div>
                                {showBadge && (
                                    <div className={styles.row_badge}>
                                        <Status variant={txnStatusVariant(txn.status)}>
                                            {txnStatusLabel(txn.status)}
                                        </Status>
                                    </div>
                                )}
                                <p
                                    className={[
                                        styles.row_amount,
                                        refund ? styles.row_amount_debit : styles.row_amount_credit
                                    ].join(' ')}>
                                    {refund ? '-' : '+'}$
                                    {prettifyAmount(Number(txn.amount).toFixed(2))}
                                </p>
                            </div>
                        );
                    })}
                    {isLoading && transactions.length > 0 &&
                        Array.from({ length: 3 }).map((_, i) => (
                            <div key={`sk-more-${i}`} className={styles.row_skeleton}>
                                <Skeleton className={styles.skeleton_avatar} />
                                <div className={styles.skeleton_text_block}>
                                    <Skeleton className={styles.skeleton_line_primary} />
                                    <Skeleton className={styles.skeleton_line_secondary} />
                                </div>
                                <Skeleton className={styles.skeleton_amount} />
                            </div>
                        ))}
                </div>
            </div>
            <div ref={loader} />
            {isFilterOpen && (
                <FilterDrawer<ETransactionStatus>
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    movement={{ value: movement, onChange: setMovement }}
                    statusChips={PAYMENTS_STATUS_CHIPS}
                    selectedStatuses={statusChips}
                    sortDir={sortDir}
                    onDateFrom={setDateFrom}
                    onDateTo={setDateTo}
                    onToggleStatus={handleToggleStatus}
                    onSortDir={setSortDir}
                    onReset={handleFilterReset}
                    onClose={handleFilterClose}
                />
            )}
            {selectedTxnEntry && (
                <PurchaseDetailDrawer
                    txn={selectedTxnEntry}
                    order={paymentsTxnOrderInfo(selectedTxnEntry)}
                    onClose={handleCloseTransactionDrawer}
                    onViewOrder={() => {
                        setSelectedTxnEntry(null);
                        navigate(routes.developers_orders);
                    }}
                />
            )}
        </motion.div>
    );
};

export default DevelopersPaymentsLayout;
